import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "src/store";
import { formatJsonResponse } from "src/stores/vs-product";
import { API_PROD_URL } from "src/utils/axios";
import { interactiveReportInput } from "./interactive-report/action";
import { dataSourceInvestment, fieldLevelDiagnostic, fileuploadresp, objectiveConfirmation, proposedCleanup, proposedVisuals, quickHealth, schemaValidation } from "./__mock__";
import { DragControls } from "framer-motion";

export interface GenerateImageParams {
  context: string;
  prompt: string;
  base_image: any; // optional
  additional_instructions?: string; // optional
  preference?: "gemini"; // adjust if more options exist
}

export interface GenerateImageResponse {
  image_url: string; // base64 image string
  // add more fields if backend returns extra metadata
}

// types.ts (or same file if you prefer)
export interface SaveEmailParams {
  userId: string;
  threadId: string;
  email: string;
}


export const sendAiAgentQuery = createAsyncThunk(
  "sendQuery",
  async (
    {
      agentName,
      user_input,
      user_id,
      thread_id,
      sendPitchData,
      file_upload_status,
      shouldRename,
      step,
      jsonType,
    }: {
      agentName?: string;
      user_input: string;
      user_id: string;
      thread_id: string;
      sendPitchData?: boolean;
      file_upload_status?: boolean;
      shouldRename?: boolean;
      step?: number;
      jsonType?: string;
    },
    { getState, dispatch },
  ): Promise<any> => {

    // return {
    //   data: fieldLevelDiagnostic,
    // }

    const ipExperts: Record<string, string> = {
      "Prior Art Agent": "prior_art_agent",
      "FTO Agent": "fto_agent",
      "Patent Licensing Agent": "patent-license-agent",
      "Patent Landscape Agent": "landscape_agent",
      "Prosecution History Agent": "file_wrapper_agent",
    };

    const bfsiAgents: Record<string, string> = {
      "Risk Horizon Mapping Agent": "risk-horizon-mapping-agent",
      "Financial Portfolio Optimization Agent": "financial-portfolio-optimization-agent",
      "Regulatory Impact Forecasting Agent": "regulatory-impact-forecasting-agent",
      "Emerging Market Opportunity Agent": "emerging-market-opportunity-agent",
      "Vendor and Partner Risk Assessment Agent": "vendor-and-partner-risk-assessment-agent",
      "M and A Opportunity Mapping Agent": "ma-opportunity-mapping-agent",
      "Payment Fraud Monitoring Agent": "payment-fraud-monitoring-agent",
      "Transactional Risk Pattern Agent": "transactional-risk-pattern-agent",
      "Invention Diligence Agent": "invention-diligence-agent",
      "Commercialization Strategy Agent": "commercialization-strategy-agent",
      "Licensing Opportunity Agent": "licensing-opportunity-agent",
      "Startup Spinout Support Agent": "startup-spinout-support-agent",
      "Technology Readiness and Adoption Agent": "technology-readiness-and-adoption-agent",
      "ESG Compliance Tracker Agent": "esg-compliance-tracker-agent",
      "Sustainability Risk Assessment Agent": "sustainability-risk-assessment-agent",
      "Green Innovation Opportunity Agent": "green-innovation-opportunity-agent",
      "Carbon Market Intelligence Agent": "carbon-market-intelligence-agent",
      "Vendor Risk Mapping Agent": "vendor-risk-mapping-agent",
      "Operational Efficiency Diligence Agent": "operational-efficiency-diligence-agent",
      "Third-Party Risk Monitoring Agent": "third-party-risk-monitoring-agent",
    };

    const customerAgents: Record<string, string> = {
      "Customer 360 Agent": "customer-360-agent",
    }

    const investmentAgents: Record<string, string> = {
      "Investment Diligence Agent": "investment-diligence-agent",
    }

    const marketingAgents: Record<string, string> = {
      "Marketing Agent": "marketing-agent",
    };
    const liebermanAgents: Record<string, string> = {
      "MR Insights Agent": "mr-insights-agent",
    };

    const newslettersAgents: Record<string, string> = {
      "Email Automation Agent": "newsletter-agent",
    };

    const qualitativeAgentV2: Record<string, string> = {
      "Qualitative Agent - V2": "qualitative-agent-v2",
    };

     const qualitativeAgentV3: Record<string, string> = {
      "Qualitative Agent - V3": "qualitative-agent-v3",
    };

    const quantitativeAgents: Record<string, string> = {
      "Quantitative Agent": "quantitative-agent",
    };

    const reportAnythingAgents: Record<string, string> = {
      "Report Anything Agent":"report-anything-agent",
    };

    const whitepaperGenerationAgents: Record<string, string> = {
      "Whitepaper Generation Agent":"whitepaper-generation-agent",
    };

    const state = getState() as RootState;
    const pitchdeckData = state.VSProduct.pitchdeck_data;
    const fileData = state.VSProduct?.fileData;

    const bodyData: any = {
      userId: String(user_id), // Convert userId to string
      threadId: String(thread_id),
      industry: Object.keys(ipExperts).includes(agentName || "")
        ? agentName
        : agentName === "Investment Diligence Agent"
          ? "Climate Technology"
          : agentName === "Marketing Agent"
            ? "Marketing"
            : agentName === "MR Insights Agent"
              ? "CAR-T"
              : "AI",
      agent: agentName
        ? Object.keys(ipExperts).includes(agentName || "")
          ? ipExperts[agentName]
          : Object.keys(liebermanAgents).includes(agentName || "")
            ? liebermanAgents[agentName]
            : Object.keys(customerAgents).includes(agentName || "")
              ? "customer_360_agent"
              : Object.keys(marketingAgents).includes(agentName || "")
                ? marketingAgents[agentName]
                : Object.keys(investmentAgents).includes(agentName || "")
                  ? investmentAgents[agentName]
                  : agentName || "Startup Diligence Agent"
        : "Startup Diligence Agent",
      useCase: agentName === "Patent Licensing Agent"
        ? "licensing-out"
        : agentName === "Investment Diligence Agent"
          ? "Help Sabancı Climate Ventures perform structured, intelligent due diligence on climate technology startups, with dynamic, context-aware guidance at each step"
          : agentName === "Marketing Agent"
            ? "Analyze marketing strategies, campaigns, and market opportunities"
            : agentName === "MR Insights Agent"
              ? "Analyze MR Insights trends, clinical and commercial landscape, and provide actionable intelligence"
              : "AI",
        step: Object.keys(quantitativeAgents).includes(agentName || "")
        ? (step === 1 ? step + 1 : step)
        : 0,
      data: { user_input: sendPitchData ? JSON.stringify(pitchdeckData) : user_input },
      file_upload_status: file_upload_status ? file_upload_status : false,
      ...(sendPitchData
        ? { file_upload_url: fileData }
        : {}),
    };

    if (jsonType === "Data_sources") {
      bodyData.additional_data_sources = state.VSProduct.additionalDataSources || null;
    } else if (jsonType === "Final_report") {
      bodyData.additional_report_template = state.VSProduct.additionalReportTemplate || null;
    } else if (jsonType === "campaign_template") {
      // bodyData.additional_report_template = state.VSProduct.additionalReportTemplate || null;
   
    }

    const endpoint = Object.keys(investmentAgents).includes(agentName || "")
      ? `${API_PROD_URL}/investment-agent/process-step`
      : Object.keys(marketingAgents).includes(agentName || "")
        ? `${API_PROD_URL}/marketing-agent/process-step`
        : Object.keys(liebermanAgents).includes(agentName || "")
          ? `${API_PROD_URL}/cart-adoption-intelligence-agent-v2/process-step`
          : `${API_PROD_URL}${Object.keys(ipExperts).includes(agentName || "")
            ? "/fto-agent"
            : Object.keys(bfsiAgents).includes(agentName || "")
              ? "/bfsi-agent"
              : Object.keys(customerAgents).includes(agentName || "")
                ? "/Customer_agents"
                : Object.keys(newslettersAgents).includes(agentName || "")
                  ? "/newsletter-agent-v2"
                  : Object.keys(qualitativeAgentV2).includes(agentName || "")
                    ? "/quant-agent-v2"
                    : Object.keys(quantitativeAgents).includes(agentName || "")
                      ? "/quant_agents"
                      : Object.keys(qualitativeAgentV3).includes(agentName || "")
                      ? "/quant-agent-v3"
                      : Object.keys(reportAnythingAgents).includes(agentName || "")
                      ? "/report-on-anything-v3"
                      : Object.keys(whitepaperGenerationAgents).includes(agentName || "")
                      ? "/whitepaper-agent-v1"
                      : ""
          }/process-step`;

    const response: any = await axios.post(endpoint, bodyData, {
      headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
      responseType: "stream",
    });

    if (sendPitchData) {
      return response;
    } else {
      if (response.data?.type_json === "proposed_visuals" || response.data?.type_json === "schema_validation_field_level_diagnostic") {
        return {
          ...response,
          data: {
            ...response.data,
            response: `${response.data.response} @@Continue@@`
          }
        }
      }

      return response
    }
  }
)

export const endChatThread = createAsyncThunk(
  "chatEnded",
  async (
    {
      user_id,
      thread_id,
    }: {
      user_id: string;
      thread_id: string;
    },
    { getState },
  ): Promise<any> => {
    const response: any = await axios.patch(
      `${API_PROD_URL}/update_thread_complete/?user_id=${user_id}&thread_id=${thread_id}`,
      {},
      { headers: { "Content-Type": "application/json" }, responseType: "stream" },
    );
    return response;
  },
);

export const submitCustomizeReport = createAsyncThunk(
  "submitCustomizeReport",
  async (payload: any, { getState }): Promise<any> => {
    const response: any = await axios.post(`${API_PROD_URL}/api/threads/report-config`, payload, {
      headers: { "Content-Type": "application/json" },
      responseType: "stream",
    });
    return response;
  },
);

export const submitGuestUser = createAsyncThunk(
  "submitGuestUser",
  async (payload: { email: string }, { getState }): Promise<any> => {
    const response: any = await axios.post(`${API_PROD_URL}/guest/register`, payload, {
      headers: { "Content-Type": "application/json" },
      responseType: "stream",
    });
    return response;
  },
);

export const fetchCustomizeReport = async (
  thread_id: string,
  userId: string,
  callback: (value: { customReport: any; loading: boolean }) => void,
) => {
  try {
    const response = await fetch(
      `${API_PROD_URL}/api/threads/${thread_id}/report-config?user_id=${userId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );

    if (response.ok) {
      const data = await response.json();
      callback({
        customReport: data || {},
        loading: false,
      });
    }
  } catch (err) {
    console.error(err);
    callback({
      customReport: {},
      loading: false,
    });
  } finally {
    // setLoading(false);
  }
};

export const mapColumns = createAsyncThunk(
  "post-map-columns",
  async (payload: { file: File }, { getState }): Promise<any> => {

    const formData = new FormData();
    formData.append("file", payload.file);

    const response: any = await axios.post(`${API_PROD_URL}/map-columns/`, formData, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

export const mapColumnsChat = createAsyncThunk(
  "post-map-columns-chat",
  async (payload: { file: File, userId: string, threadId: string, include_plot: boolean, query: string }, { getState }): Promise<any> => {

    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("query", payload.query);

    const response: any = await axios.post(`${API_PROD_URL}/chat/?user_id=${payload.userId}&thread_id=${payload.threadId}&include_plot=${payload.include_plot || false}`, formData, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

export const objectiveConfirmed = createAsyncThunk(
  "post-objective-confirmed",
  async (payload: { file: File, }, { getState }): Promise<any> => {

    const formData = new FormData();
    formData.append("file", payload.file);

    const response: any = await axios.post(`${API_PROD_URL}/confirm-objective/`, formData, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

export const anaylzeChatBotChecklist = createAsyncThunk(
  "post-analyze-chatbot",
  async (payload: { files: File[], userId: string }, { getState }): Promise<any> => {

    const formData = new FormData();
    payload.files.forEach((file) => {
      formData.append("files", file);
    })

    const response: any = await axios.post(`${API_PROD_URL}/analyze_chatbot_files/?user_id=${payload.userId}`, formData, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

export const updateObjectives = createAsyncThunk(
  "put-update-objectives",
  async (payload: { objectives: any, userId: string, threadId: string }): Promise<any> => {

    const response: any = await axios.put(`${API_PROD_URL}/investment-agent/update-objectives?user_id=${payload.userId}&thread_id=${payload.threadId}`, payload.objectives, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

export const updateCleanup = createAsyncThunk(
  "put-update-cleanup",
  async (payload: { objectives: any, userId: string, threadId: string }): Promise<any> => {

    const response: any = await axios.post(`${API_PROD_URL}/investment-agent/update-cleanup?user_id=${payload.userId}&thread_id=${payload.threadId}`, payload.objectives, {
      headers: { "Content-Type": "application/json" },
    });
    return response;
  },
);

// Add this to your actions file
export const submitMarketingCampaign = createAsyncThunk(
  'marketing-agent/process-template',
  async (payload: {
    userId: string;
    threadId: string;
    jsonResponse: any;
    // Add other required fields
  }) => {
    const response = await axios.post(
      `${API_PROD_URL}/marketing-agent/process-template?userId=${payload.userId}&threadId=${payload.threadId}`,
      payload.jsonResponse, // Send the JSON response directly as the request body
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  }
);


export const quantAgnetUploadFiles = createAsyncThunk(
  "quant_agent/step1/upload",
  async (payload: {
    user_id: string;
    thread_id: string;
    excel_file: File;
    guide_file: File;
  }) => {
    const formData = new FormData();

    formData.append("user_id", payload.user_id);
    formData.append("thread_id", payload.thread_id);
    formData.append("excel_file", payload.excel_file);
    formData.append("guide_file", payload.guide_file);

    const response = await axios.post(
      `${API_PROD_URL}/quant_agent/step1/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  }
);

export const initQuantChat = createAsyncThunk(
  "quant_agent/init",
  async (

  ) => {

    const response: any = await axios.get(`${API_PROD_URL}/quant_agent/init`, {
      headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
    })

  

    return response
  }
)

export const generatePPTFromText = createAsyncThunk(
  "generate-from-text-ai",
  async (text: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_PROD_URL}/generate-from-text-ai`,
        { text },
        {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
          responseType: "blob", // IMPORTANT: API returns the PPT file
        }
      );



      return response.data; // This is the PPT file Blob
    } catch (error: any) {
      console.error("PPT Generation Error:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const generatePPTV2 = createAsyncThunk(
  "quant-agent-v2/generate-ppt",
  async (params: GeneratePPTParams, { rejectWithValue }) => {
    try {
      const { userId, threadId, version = "v1" } = params;
      
      const response = await axios.get(
        `${API_PROD_URL}/quant-agent-v2/generate-ppt`,
        {
          params: {
            userId,
            threadId,
            version,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

  

      return response.data; 
    } catch (error: any) {
      console.error("PPT Generation Error:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const generatePPTV3 = createAsyncThunk(
  "quant-agent-v3/generate-ppt",
  async (params: GeneratePPTParams, { rejectWithValue }) => {
    try {
      const { userId, threadId, version = "v1" } = params;
      
      const response = await axios.get(
        `${API_PROD_URL}/quant-agent-v3/generate-ppt`,
        {
          params: {
            userId,
            threadId,
            version,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

  

      return response.data; 
    } catch (error: any) {
      console.error("PPT Generation Error:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const getPPTStatusV2 = createAsyncThunk(
  "quant-agent-v2/ppt-status",
  async (params: GetPPTStatusParams, { rejectWithValue }) => {
    try {
      const { userId, threadId, workflowId } = params;
      
      const response = await axios.get(
        `${API_PROD_URL}/quant-agent-v2/ppt-status`,
        {
          params: {
            userId,
            threadId,
            workflowId,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

   

      return response.data; // Returns JSON status object
    } catch (error: any) {
      console.error("PPT Status Error:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const getPPTStatusV3 = createAsyncThunk(
  "quant-agent-v3/ppt-status",
  async (params: GetPPTStatusParams, { rejectWithValue }) => {
    try {
      const { userId, threadId, workflowId } = params;
      
      const response = await axios.get(
        `${API_PROD_URL}/quant-agent-v3/ppt-status`,
        {
          params: {
            userId,
            threadId,
            workflowId,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

    

      return response.data; // Returns JSON status object
    } catch (error: any) {
      console.error("PPT Status Error:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);


export const generateWhitepaperImage = createAsyncThunk<
  GenerateImageResponse,
  GenerateImageParams,
  { rejectValue: any }
>(
  "whitepaper-agent-v1/generate-image",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_PROD_URL}/whitepaper-agent-v1/generate-image`,
        {
          context: params.context,
          prompt: params.prompt,
          base_image: params.base_image,
          additional_instructions: params.additional_instructions,
          preference: params.preference ?? "gemini",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      

      return response.data;
    } catch (error: any) {
      console.error("Generate Image Error:", error);
      return rejectWithValue(
        error.response?.data || "Failed to generate image"
      );
    }
  }
);

export const saveEmail = createAsyncThunk(
  "consulting-agent/save-email",
  async (params: SaveEmailParams, { rejectWithValue }) => {
    try {
      const { userId, threadId, email } = params;

      const response = await axios.post(
        `${API_PROD_URL}/save-email`,
        null, // 👈 no body, params only
        {
          params: {
            user_id: userId,
            thread_id: threadId,
            email: email,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

    

      return response.data;
    } catch (error: any) {
      console.error("Save Email Error:", error);
      return rejectWithValue(
        error.response?.data || "Failed to save email"
      );
    }
  }
);
